/**
 * Access methods list page. Org-grouped like Servers, with a kind badge per
 * row and a one-line settings summary so an operator can scan what each
 * method points at without clicking through.
 */
import {
  Network,
  Plus,
  Loader2,
  Building2,
  ChevronRight,
  Terminal,
  Container,
  Layers,
  Cpu,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { CreateAccessMethodDialog } from '@/components/applications/CreateAccessMethodDialog.js';
import { api, type AccessMethod, type AccessMethodKind } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';
import { cn } from '@/lib/utils.js';

export function AccessMethodsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { memberships } = useAuth();

  const query = useQuery({
    queryKey: ['access-methods'],
    queryFn: () => api.accessMethods.listAll(),
  });

  const byOrg = useMemo(() => {
    const m = new Map<string, { orgName: string; orgSlug: string; items: AccessMethod[] }>();
    for (const am of query.data ?? []) {
      if (!m.has(am.orgId)) m.set(am.orgId, { orgName: am.orgName, orgSlug: am.orgSlug, items: [] });
      m.get(am.orgId)!.items.push(am);
    }
    return m;
  }, [query.data]);

  const reachableByOrg = useMemo(() => {
    const out: Record<string, AccessMethod[]> = {};
    for (const am of query.data ?? []) {
      (out[am.orgId] ??= []).push(am);
    }
    return out;
  }, [query.data]);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold tracking-tight">Access methods</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            How DeployIt reaches a target. Compose them (Docker over SSH, kubectl via a bastion)
            to model real access topologies.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={memberships.length === 0}>
          <Plus className="h-4 w-4" />
          New access method
        </Button>
      </header>

      {query.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Could not load access methods.
          </CardContent>
        </Card>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} />
      ) : (
        <div className="space-y-6">
          {[...byOrg.entries()].map(([orgId, group]) => (
            <section key={orgId} className="space-y-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <Link to="/orgs/$slug" params={{ slug: group.orgSlug }} className="hover:text-foreground">
                  {group.orgName}
                </Link>
                <span className="font-mono normal-case">/{group.orgSlug}</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.items.map((am) => (
                  <AccessMethodCard key={am.id} am={am} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <CreateAccessMethodDialog
        open={open}
        onOpenChange={setOpen}
        memberships={memberships}
        reachableMethodsByOrg={reachableByOrg}
        onCreated={async () => {
          await queryClient.invalidateQueries({ queryKey: ['access-methods'] });
        }}
      />
    </div>
  );
}

function AccessMethodCard({ am }: { am: AccessMethod }): JSX.Element {
  return (
    <Link
      to="/p/applications/access-methods/$id"
      params={{ id: am.id }}
      search={{ org: am.orgSlug }}
      className="block"
    >
      <Card className="h-full transition-colors hover:border-primary/60">
        <CardHeader className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base">{am.name}</CardTitle>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <CardDescription className="text-xs">
            <KindBadge kind={am.kind} />
            <span className="ml-2 font-mono opacity-60">/{am.slug}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 pt-0">
          <p className="font-mono text-[11px] text-muted-foreground">{settingsSummary(am)}</p>
          {am.transportAccessMethodId && (
            <p className="text-[11px] text-muted-foreground">
              via another access method
            </p>
          )}
          {am.description && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{am.description}</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function settingsSummary(am: AccessMethod): string {
  const s = am.settings ?? {};
  switch (am.kind) {
    case 'ssh':
      return `${s['user'] ?? '?'}@${s['host'] ?? '?'}${s['port'] && s['port'] !== 22 ? `:${s['port']}` : ''}`;
    case 'docker':
      return `docker exec ${s['container'] ?? '?'}${s['user'] ? ` (as ${s['user']})` : ''}`;
    case 'kubectl':
      return `kubectl --context=${s['context'] ?? '?'}${
        s['namespace'] ? ` -n ${s['namespace']}` : ''
      }${s['pod'] ? ` ${s['pod']}` : ''}`;
    case 'local':
      return 'local on DeployIt host';
  }
}

export function KindBadge({ kind }: { kind: AccessMethodKind }): JSX.Element {
  const styles: Record<AccessMethodKind, { dot: string; Icon: typeof Terminal }> = {
    ssh: { dot: 'bg-sky-500/10 text-sky-700 dark:text-sky-300', Icon: Terminal },
    docker: { dot: 'bg-blue-500/10 text-blue-700 dark:text-blue-300', Icon: Container },
    kubectl: { dot: 'bg-violet-500/10 text-violet-700 dark:text-violet-300', Icon: Layers },
    local: { dot: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300', Icon: Cpu },
  };
  const { dot, Icon } = styles[kind];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        dot,
      )}
    >
      <Icon className="h-2.5 w-2.5" />
      {kind}
    </span>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Network className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-medium">No access methods yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Define how DeployIt reaches a server. The most common starting point is an SSH leaf;
          docker/kubectl layers go on top.
        </p>
        <Button onClick={onCreate} className="mt-4">
          <Plus className="h-4 w-4" />
          New access method
        </Button>
      </CardContent>
    </Card>
  );
}
